import React from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { normalizeFaSearchText } from '@/lib/textNormalize';

export type SearchableOption = {
  value: string;
  label: string;
  searchText?: string;
  suggested?: boolean;
};

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /**
   * داخل مودال: بدون Portal تا با اسکرول جابه‌جا نشود و از مودال بیرون نزند.
   */
  inModal?: boolean;
  /** برای نمایش بالای مودال‌های لایه‌بالا (مثلاً z-[110]) */
  popoverClassName?: string;
  /** هنگام باز شدن لیست، جست‌وجو با این متن پر می‌شود */
  initialSearchQuery?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'انتخاب...',
  emptyMessage = 'موردی یافت نشد',
  disabled,
  className,
  inModal = false,
  popoverClassName,
  initialSearchQuery,
}) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setQ(initialSearchQuery?.trim() || '');
      } else {
        setQ('');
      }
    },
    [initialSearchQuery],
  );

  React.useEffect(() => {
    if (!open || !inModal) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        handleOpenChange(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, inModal, handleOpenChange]);

  const selected = React.useMemo(() => options.find((x) => x.value === value), [options, value]);
  const filtered = React.useMemo(() => {
    const s = normalizeFaSearchText(q);
    if (!s) return options;
    return options.filter((x) => {
      const haystack = normalizeFaSearchText(`${x.label} ${x.searchText || ''}`);
      return haystack.includes(s);
    });
  }, [options, q]);

  const pick = (next: string) => {
    onChange(next);
    handleOpenChange(false);
  };

  const list = (
    <>
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pr-10"
          placeholder="جست‌وجو..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus={inModal}
        />
      </div>
      <div className="max-h-48 overflow-y-auto overscroll-contain rounded-md border border-border bg-card">
        {filtered.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={cn(
              'flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-start text-sm last:border-b-0 hover:bg-muted',
              opt.value === value && 'bg-sky-50 font-medium text-sky-800',
            )}
            onClick={() => pick(opt.value)}
          >
            <span className="min-w-0 truncate">{opt.label}</span>
            {opt.suggested ? (
              <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                پیشنهاد
              </span>
            ) : null}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="p-4 text-center text-sm text-muted-foreground">{emptyMessage}</p>
        )}
      </div>
    </>
  );

  if (inModal) {
    return (
      <div ref={rootRef} className="relative min-w-0 w-full">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            'flex h-10 w-full min-w-0 max-w-full justify-between gap-2 overflow-hidden font-normal whitespace-normal',
            className,
          )}
          onClick={() => {
            const next = !open;
            handleOpenChange(next);
            if (next) {
              requestAnimationFrame(() => {
                rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
              });
            }
          }}
        >
          <span className="min-w-0 flex-1 truncate text-start">{selected?.label ?? placeholder}</span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-60 transition', open && 'rotate-180')} />
        </Button>
        {open ? (
          <div
            className={cn(
              'mt-1 rounded-md border border-border bg-white p-3 text-popover-foreground shadow-md',
              popoverClassName,
            )}
            dir="rtl"
          >
            {list}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-w-0 w-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'flex h-10 w-full min-w-0 max-w-full justify-between gap-2 overflow-hidden font-normal whitespace-normal',
              className,
            )}
          >
            <span className="min-w-0 flex-1 truncate text-start">{selected?.label ?? placeholder}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          collisionPadding={16}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn('z-50 w-[min(100vw-2rem,28rem)] p-3', popoverClassName)}
          dir="rtl"
        >
          {list}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SearchableSelect;
